import { useEffect, useState, useCallback, useRef } from "react";
import api from "../api";
import { Product } from "../pages/ProductSections/ProductItem";
import { useSearchParams } from "react-router-dom";
export type FetchType = "category" | "cart" | "favourites" | "orders" | "search";
 
const TAKE = 20;
const PAGINATED: FetchType[] = ["category", "search"];
 
export function useProducts(type: FetchType, id?: string, query?: string) {
  const [products, setProducts]     = useState<Product[]>([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [hasMore, setHasMore]       = useState(false);
  const skipRef                     = useRef(0);
  const [searchParams]              = useSearchParams();    
  const isPaginated = PAGINATED.includes(type);
  
  // Get the actual search query from URL or prop
  const actualQuery = searchParams.get("q") || query || "";
 
  const fetchPage = useCallback(async (pageSkip: number, replace: boolean) => {
    try {
      replace ? setLoading(true) : setLoadingMore(true);
      setError(null);
 
      switch (type) {
 
        case "category": {
          if (!id) throw new Error("Category ID required");
          const res = await api.getProductsByCategory(id);
          const data: Product[] = res.data ?? [];
          setProducts(prev => replace ? data : [...prev, ...data]);
          setHasMore(data.length === TAKE);
          break;
        }
 
        case "search": {
          if (!actualQuery?.trim()) {
            setProducts([]);
            setHasMore(false);
            break;
          }
          const res = await api.searchProducts(actualQuery.trim(), pageSkip, TAKE);
          const data: Product[] = (res as any).data ?? [];
          setProducts(prev => replace ? data : [...prev, ...data]);
          setHasMore(data.length === TAKE);
          break;
        }
 
        case "cart": {
          const res: any = await api.getCart();
          if (Array.isArray(res?.items)) {
            // Group cart items by product ID to combine multiple sizes of the same product
            const groupedItems = res.items.reduce((acc: any, item: any) => {
              const productId = item.product.id;
              
              if (!acc[productId]) {
                // First time seeing this product, create entry
                acc[productId] = {
                  ...item.product,
                  cartItemId: item.id,
                  cartPrice: item.price,
                  cartQuantity: item.quantity,
                  cartTotal: item.total,
                  cartSizes: [{ size: item.size, quantity: item.quantity, cartItemId: item.id }]
                };
              } else {
                // Product already exists, add this size to the existing entry
                acc[productId].cartSizes.push({ size: item.size, quantity: item.quantity, cartItemId: item.id });
                // Update total quantity for the product
                acc[productId].cartQuantity += item.quantity;
                acc[productId].cartTotal += item.total;
              }
              
              return acc;
            }, {});
            
            setProducts(Object.values(groupedItems));
          } else {
            setProducts([]);
          }
          break;
        }
 
        case "favourites": {
          const res: any = await api.getFavourites();
          if (Array.isArray(res)) {
            setProducts(
              res.map((fav: any) => ({
                ...fav.product,
                favouriteId: fav.id,
                isFavorite: true,
              }))
            );
          } else {
            setProducts([]);
          }
          break;
        }
 
        case "orders": {
          const res = await api.getOrders();
          setProducts(Array.isArray(res) ? (res as any) : []);
          break;
        }
 
        default:
          throw new Error("Invalid fetch type");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching products");
      setProducts([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [type, id, actualQuery]); // Added actualQuery as dependency
 
  // Reset and fresh fetch whenever type / id / actualQuery changes
  useEffect(() => {
    skipRef.current = 0;
    setHasMore(false);
    fetchPage(0, true);
  }, [type, id, actualQuery]); // Changed from query to actualQuery
 
  const fetchNextPage = useCallback(() => {
    if (!isPaginated || loadingMore || !hasMore) return;
    const next = skipRef.current + TAKE;
    skipRef.current = next;
    fetchPage(next, false);
  }, [isPaginated, loadingMore, hasMore, fetchPage]);
 
  return {
    products,
    setProducts,
    loading,
    loadingMore,
    error,
    hasMore: isPaginated && hasMore,
    fetchNextPage,
  };
}
